// Set your password (spec C1, C7.1) from an invitation link: /set-password#t=<token>.
// The token sits after "#", so it never reaches a server log; opening the page only checks it,
// and "Set password and sign in" uses it. Markup from the SignInCard card, the Input card's
// password checklist and the "Sign in and system states" board.
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { Api } from '../auth/api';
import { copy, rateLimitText } from '../auth/copy';
import { tokenFromHash } from '../auth/link-token';
import { HOME } from '../auth/next';
import { passwordRules } from '../auth/password-rules';
import { Brand, CardTitle, ErrorLine, PasswordField, SignInLayout } from '../auth/SignInLayout';
import { CheckIcon } from '../shell/icons';

type LinkState = { kind: 'checking' } | { kind: 'ready'; email: string } | { kind: 'invalid' };

export function SetPasswordPage({ api }: { api: Api }) {
  const token = tokenFromHash(useLocation().hash);
  const [link, setLink] = useState<LinkState>(token ? { kind: 'checking' } : { kind: 'invalid' });

  useEffect(() => {
    if (!token) return;
    let live = true;
    api.checkLink(token).then((res) => {
      if (!live) return;
      setLink(res.ok ? { kind: 'ready', email: res.body.email } : { kind: 'invalid' });
    });
    return () => {
      live = false;
    };
  }, [api, token]);

  if (link.kind === 'ready' && token) {
    return <SetPasswordForm api={api} token={token} email={link.email} />;
  }
  return link.kind === 'checking' ? <CheckingLink /> : <LinkProblem />;
}

function CheckingLink() {
  return (
    <SignInLayout>
      <div className="lcard" aria-labelledby="setpw-title" aria-busy="true">
        <Brand />
        <CardTitle id="setpw-title" title={copy.setYourPassword}>
          {copy.checkingLink}
        </CardTitle>
      </div>
    </SignInLayout>
  );
}

export function LinkProblem({ email }: { email?: string }) {
  const navigate = useNavigate();
  return (
    <SignInLayout>
      <div className="lcard" aria-labelledby="setpw-title">
        <Brand />
        <CardTitle id="setpw-title" title={copy.setYourPassword}>
          {copy.resetIntro}
        </CardTitle>
        <ErrorLine text={copy.linkProblem} center />
        <button
          type="button"
          className="btn btn-primary wide"
          onClick={() => navigate('/forgot-password', { state: { email } })}
        >
          {copy.sendNewLink}
        </button>
        <div className="row" style={{ justifyContent: 'center' }}>
          <button
            type="button"
            className="linkbtn"
            onClick={() => navigate('/sign-in', { replace: true })}
          >
            {copy.backToSignIn}
          </button>
        </div>
      </div>
    </SignInLayout>
  );
}

type SetPasswordFormProps = { api: Api; token: string; email: string };

export function SetPasswordForm({ api, token, email }: SetPasswordFormProps) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const rules = passwordRules(password, confirm, email);
  const canSubmit = rules.every((r) => r.ok) && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const res = await api.setPassword(token, password, confirm);
    if (res.ok) {
      navigate(HOME, { replace: true });
      return;
    }
    setBusy(false);
    if (res.error === 'link_invalid') setExpired(true);
    else if (res.error === 'rate_limited') setError(rateLimitText(res));
    else setError(copy.somethingWrong);
  }

  if (expired) return <LinkProblem email={email} />;

  return (
    <SignInLayout>
      <form className="lcard" aria-labelledby="setpw-title" noValidate onSubmit={submit}>
        <Brand />
        <CardTitle id="setpw-title" title={copy.setYourPassword}>
          {copy.forEmail(email)}
        </CardTitle>
        {/* Lets a password manager save the new password under the right email. */}
        <input type="email" autoComplete="username" value={email} readOnly hidden />
        <PasswordField
          id="setpw-new"
          label={copy.newPassword}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          autoFocus
        />
        <PasswordField
          id="setpw-confirm"
          label={copy.confirmPassword}
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />
        <ul className="rules" aria-label="Password rules">
          {rules.map((rule) => (
            <li key={rule.label}>
              <span className={rule.ok ? 'rdot ok' : 'rdot'}>{rule.ok && <CheckIcon />}</span>
              <span className={rule.ok ? 'tx-green' : 'tx-muted'}>
                {rule.label}
                <span className="sr">{rule.ok ? ', done' : ', not yet'}</span>
              </span>
            </li>
          ))}
        </ul>
        {error && <ErrorLine text={error} />}
        <button type="submit" className="btn btn-primary wide" disabled={!canSubmit}>
          {copy.setAndSignIn}
        </button>
      </form>
    </SignInLayout>
  );
}
